import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  getCashStamp,
  isStampActive,
  hasAlreadyClaimed,
  remainingClaims,
  addClaim,
  incrementViews,
  type CashStamp,
} from '@/lib/cashStampStorage';
import { formatBCH } from '@/lib/bch';
import { cn } from '@/lib/utils';

type ClaimState = 'loading' | 'view' | 'claimed' | 'already-claimed' | 'expired' | 'not-found';

/**
 * Public CashStamp claim page.
 *
 * URL: /stamp/:id
 *
 * This page is visited by customers. They see the promotion details
 * and can claim the BCH reward. One claim per Nostr pubkey.
 *
 * NOTE: In V1, claims are tracked in localStorage on the merchant's device.
 * This means the claim page only works when viewed on the same device as the
 * merchant's POS. For a production multi-device setup, claims would need to
 * be stored on Nostr relays or a backend. V1 focuses on in-store use cases
 * where the merchant shows the QR/link on their own device or printed material,
 * and the claim is recorded when the customer visits and the merchant approves it.
 */
export function StampClaimPage() {
  const { id } = useParams<{ id: string }>();
  const [stamp, setStamp] = useState<CashStamp | null>(null);
  const [state, setState] = useState<ClaimState>('loading');
  const [claimAddress, setClaimAddress] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeoMeta({
    title: stamp ? `${stamp.title} — CashStamp by ${stamp.merchantName}` : 'CashStamp',
    description: stamp?.description || 'Claim free BCH from a merchant promotion',
  });

  useEffect(() => {
    if (!id) {
      setState('not-found');
      return;
    }
    const s = getCashStamp(id);
    if (!s) {
      setState('not-found');
      return;
    }
    setStamp(s);
    incrementViews(id);
    if (!isStampActive(s)) {
      setState('expired');
    } else {
      setState('view');
    }
  }, [id]);

  const handleClaim = () => {
    if (!stamp || !id) return;
    const addr = claimAddress.trim();
    if (!addr) {
      setError('Enter your BCH address to receive the reward.');
      return;
    }

    setClaiming(true);
    setError(null);

    // In V1, we use the BCH address as a pseudo-unique identifier.
    // A proper implementation would use Nostr pubkey authentication.
    const pubkey = addr.toLowerCase().replace(/^bitcoincash:/i, '');

    if (hasAlreadyClaimed(stamp, pubkey)) {
      setState('already-claimed');
      setClaiming(false);
      return;
    }

    const result = addClaim(id, {
      pubkey,
      claimAddress: addr,
      claimedAt: Date.now(),
      status: 'pending',
    });

    if (result) {
      setStamp(result);
      setState('claimed');
    } else {
      setError('Something went wrong. Please try again.');
    }
    setClaiming(false);
  };

  // Not found
  if (state === 'not-found') {
    return (
      <div className="min-h-[100dvh] bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-6">
        <div className="text-center max-w-sm mx-auto">
          <p className="text-5xl mb-4">🔍</p>
          <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-50 mb-2">CashStamp Not Found</h1>
          <p className="text-sm text-zinc-400 mb-6">This promotion doesn't exist or has been removed.</p>
          <Link to="/" className="text-sm font-bold text-[#0AC18E] hover:underline">
            ← Go to BCHPAY.Cash
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  if (state === 'loading' || !stamp) {
    return (
      <div className="min-h-[100dvh] bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-3 border-[#0AC18E] border-t-transparent animate-spin" />
      </div>
    );
  }

  const remaining = remainingClaims(stamp);

  return (
    <div className="min-h-[100dvh] bg-zinc-50 dark:bg-zinc-950 flex flex-col">
      {/* Branded header */}
      <div className="bg-[#0AC18E] px-6 py-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3">
          <span className="text-3xl">🎁</span>
        </div>
        <h1 className="text-xl font-black text-white mb-1">{stamp.title}</h1>
        <p className="text-sm text-white/80">by {stamp.merchantName}</p>
      </div>

      <div className="flex-1 px-4 py-6 max-w-lg mx-auto w-full space-y-5">
        {/* Promotion details */}
        {stamp.description && (
          <div className="bg-white dark:bg-zinc-800 rounded-2xl px-5 py-4 border border-zinc-100 dark:border-zinc-700">
            <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{stamp.description}</p>
          </div>
        )}

        {/* Reward card */}
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
          <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 px-5 py-3 border-b border-[#0AC18E]/20">
            <p className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider">Your Reward</p>
          </div>
          <div className="px-5 py-5 text-center">
            <p className="text-3xl font-black text-[#0AC18E]">{formatBCH(stamp.rewardBCH)} BCH</p>
            <p className="text-xs text-zinc-400 mt-2">
              {remaining > 0 ? `${remaining} claims remaining` : 'All claims have been taken'}
            </p>
          </div>
        </div>

        {/* Claim form / status */}
        {state === 'view' && remaining > 0 && (
          <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
                Your BCH Address
              </label>
              <input
                type="text"
                value={claimAddress}
                onChange={(e) => { setClaimAddress(e.target.value); setError(null); }}
                placeholder="bitcoincash:qr..."
                className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
              />
              <p className="text-[11px] text-zinc-400 mt-1.5">
                Open your BCH wallet, tap Receive, and paste your address here.
              </p>
            </div>

            {error && (
              <p className="text-xs text-red-500 font-medium">{error}</p>
            )}

            <button
              onClick={handleClaim}
              disabled={claiming}
              className="w-full py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all disabled:opacity-70"
            >
              {claiming ? 'Claiming...' : `🎁 Claim ${formatBCH(stamp.rewardBCH)} BCH`}
            </button>
          </div>
        )}

        {/* Already claimed */}
        {state === 'already-claimed' && (
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl px-5 py-5 border border-amber-200 dark:border-amber-800/40 text-center">
            <p className="text-3xl mb-2">⚠️</p>
            <p className="text-sm font-bold text-amber-700 dark:text-amber-300">Already Claimed</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              This address has already claimed this CashStamp. Each address can only claim once.
            </p>
          </div>
        )}

        {/* Success! */}
        {state === 'claimed' && (
          <div className="space-y-4">
            <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-2xl px-5 py-6 border border-[#0AC18E]/30 text-center">
              <p className="text-4xl mb-2">🎉</p>
              <p className="text-lg font-black text-[#0AC18E]">Claimed!</p>
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-2 leading-relaxed">
                Your claim of <span className="font-bold text-[#0AC18E]">{formatBCH(stamp.rewardBCH)} BCH</span> has been recorded.
                The merchant will send the BCH to your address.
              </p>
            </div>

            {/* Redirect buttons */}
            <div className="space-y-2">
              {stamp.websiteUrl && (
                <a
                  href={stamp.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border-2 border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/50 transition-all"
                >
                  🌐 Visit {stamp.merchantName}'s Website
                </a>
              )}
              {stamp.bchLocalUrl && (
                <a
                  href={stamp.bchLocalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold bg-white dark:bg-zinc-800 text-purple-600 dark:text-purple-300 border-2 border-purple-200 dark:border-purple-700 hover:border-purple-400 transition-all"
                >
                  📍 Find Us on BCH Local
                </a>
              )}
            </div>
          </div>
        )}

        {/* Expired */}
        {state === 'expired' && (
          <div className="bg-zinc-100 dark:bg-zinc-800 rounded-2xl px-5 py-5 border border-zinc-200 dark:border-zinc-700 text-center">
            <p className="text-3xl mb-2">⏰</p>
            <p className="text-sm font-bold text-zinc-600 dark:text-zinc-300">Promotion Ended</p>
            <p className="text-xs text-zinc-400 mt-1">
              {stamp.disabled
                ? 'This CashStamp has been paused by the merchant.'
                : Date.now() > stamp.expiresAt
                ? 'This CashStamp has expired.'
                : 'All claims have been taken.'}
            </p>
          </div>
        )}

        {/* Expiry info */}
        <p className="text-[10px] text-zinc-300 dark:text-zinc-600 text-center">
          {state !== 'expired' && `Expires ${new Date(stamp.expiresAt).toLocaleDateString()}`}
          {' · '}Powered by <a href="https://bchpay.cash" className="text-[#0AC18E] font-semibold hover:underline">BCHPAY.Cash</a>
        </p>
      </div>
    </div>
  );
}
